import { Asset, FolderContent } from '@/api/generatedApi';
import { Box, ListItemIcon, ListItemText } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { RemoveAssetFromGroupModal } from '../modals/RemoveAssetFromGroupModal';
import { StyledList, StyledListItem } from '@/components/AssetsPage/PopoverList';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Download as DownloadIcon } from '@mui/icons-material';
import { CopyMenuItem } from '@/components/ContextMenu/CopyMenuItem';
import { OpenInNewTabMenuItem } from '@/components/ContextMenu/OpenInNewTabMenuItem';
import { CopiableColumn } from '@/types/copiableColumn';
import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { GridBaseColDef } from '@mui/x-data-grid/internals';
import { useDownloadAsset } from '@/hooks/useDownloadAsset';

export const ReportContextMenu = ({
  anchorPosition,
  onClose,
  clickedItem,
  clickedField,
  columns,
  reportId,
  groupId,
  assetCount,
  onRemoved,
  canManageGroups = true,
}: {
  anchorPosition: { left: number; top: number } | undefined;
  onClose: () => void;
  clickedItem: FolderContent | undefined;
  clickedField?: string;
  columns?: (GridBaseColDef<GridValidRowModel> & Partial<CopiableColumn<GridValidRowModel>>)[];
  reportId: string;
  groupId: string;
  assetCount: number;
  onRemoved?: () => void;
  canManageGroups?: boolean;
}) => {
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [itemToRemove, setItemToRemove] = useState<FolderContent | undefined>();
  const contextMenuReference = useRef<HTMLDivElement>(null);

  const { downloadAsset } = useDownloadAsset();

  const isAsset = !!clickedItem && clickedItem.contentType === 'asset';
  const showGroupActions = canManageGroups && isAsset;

  const copyValue =
    clickedItem && clickedField && columns
      ? columns.find(col => col.field === clickedField)?.getCopyValue?.(clickedItem)
      : undefined;

  const showContextMenu = isAsset || !!copyValue;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuReference.current && !contextMenuReference.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (anchorPosition) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorPosition, onClose]);

  const handleDownloadClick = async () => {
    if (clickedItem?.contentType === 'asset') {
      onClose();
      await downloadAsset(clickedItem as Asset);
    }
  };

  const handleRemoveClick = () => {
    if (clickedItem?.contentType === 'asset') {
      setItemToRemove(clickedItem);
      setIsRemoveModalOpen(true);
    }
    onClose();
  };

  const handleRemoveModalClose = () => {
    setIsRemoveModalOpen(false);
    setItemToRemove(undefined);
  };

  const handleRemove = () => {
    setIsRemoveModalOpen(false);
    setItemToRemove(undefined);
    onRemoved?.();
  };

  return (
    <>
      {anchorPosition && showContextMenu && (
        <Box
          sx={{
            position: 'fixed',
            left: anchorPosition.left,
            top: anchorPosition.top,
            zIndex: 1300,
            backgroundColor: 'background.paper',
            borderRadius: '12px',
            minWidth: '160px',
            boxShadow: 8,
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
            overflow: 'hidden',
          }}
          ref={contextMenuReference}
        >
          <StyledList sx={{ padding: 0 }}>
            {isAsset && clickedItem && <OpenInNewTabMenuItem item={clickedItem} onClose={onClose} />}
            {!!copyValue && columns && (
              <CopyMenuItem columns={columns} row={clickedItem} field={clickedField} onCopy={onClose} />
            )}
            {isAsset && (
              <StyledListItem onClick={() => void handleDownloadClick()}>
                <ListItemIcon>
                  <DownloadIcon sx={{ fontSize: '18px' }} />
                </ListItemIcon>
                <ListItemText>Download</ListItemText>
              </StyledListItem>
            )}
            {showGroupActions && (
              <StyledListItem onClick={handleRemoveClick}>
                <ListItemIcon>
                  <RemoveCircleOutlineIcon sx={{ fontSize: '18px' }} />
                </ListItemIcon>
                <ListItemText>Remove from Group</ListItemText>
              </StyledListItem>
            )}
          </StyledList>
        </Box>
      )}
      {itemToRemove?.contentType === 'asset' && (
        <RemoveAssetFromGroupModal
          isOpen={isRemoveModalOpen}
          onClose={handleRemoveModalClose}
          onRemove={handleRemove}
          asset={itemToRemove}
          reportId={reportId}
          groupId={groupId}
          assetCount={assetCount}
        />
      )}
    </>
  );
};
